/**
 * WebTorrentEngine — browser implementation of TorrentEngine.
 *
 * Uses WebTorrent for browser-based BitTorrent over WebRTC.
 *
 * Key architectural decisions:
 * 1. Multi-file: passes full File[] array to WebTorrent.seed()
 * 2. Download destination: uses FileSystemDirectoryHandle (chosen BEFORE download)
 * 3. Streaming: uses ReadableStream, NOT getBlob() (no full-file RAM buffering)
 * 4. Auto-seeding: WebTorrent automatically seeds after download — no copy/reseed
 * 5. Events: coalesced at ~300ms to prevent flooding the UI
 * 6. Actual byte counters: torrent.downloaded/uploaded, NOT progress * totalBytes
 *
 * Storage architecture (verified, WebTorrent 3.0.21):
 *   Browser field: fs-chunk-store → fsa-chunk-store (File System Access API, OPFS)
 *   fsa-chunk-store stores torrent pieces in the Origin Private File System.
 *   Pieces survive page reload — enabling efficient session recovery.
 *
 *   This is INDEPENDENT of BitDrop's own persistence:
 *     WebTorrent piece store  → fsa-chunk-store (OPFS)         [WebTorrent owns this]
 *     BitDrop session history → IndexedDB (HistoryRepository)   [BitDrop owns this]
 *     BitDrop dest handles   → IndexedDB (IDBHandles)           [BitDrop owns this]
 *   These three systems must never be conflated.
 *
 * Browser networking:
 * - WebRTC data channels only (browsers cannot use TCP/UDP BitTorrent)
 * - WSS trackers only (wss://)
 * - Browser-to-browser peers only (no standard BitTorrent peers)
 * - Web↔Mobile requires libtorrent WebTorrent extension on mobile side
 *
 * Stream lifecycle ownership:
 * - WebTorrent owns: piece selection, SHA-1 verification, peer management,
 *   fsa-chunk-store (OPFS) piece storage, swarm, WebRTC transport.
 * - BitDrop owns: FileSystemWritableFileStream lifecycle (open, pipe, close/abort),
 *   pause/resume/cancel propagation to active disk-write operations.
 *
 * Multi-session invariant:
 *   sessionMap: Map<engineTorrentId, Set<sessionId>>
 *   The underlying engine torrent must remain alive until ALL BitDrop sessions
 *   that map to it are removed. Removing one session must not destroy the torrent
 *   if another session still depends on the same infoHash.
 */

import WebTorrent from 'webtorrent';
import type {
  TorrentEngine,
  TorrentEngineConfig,
  TorrentInput,
  TorrentSource,
  TorrentDestination,
  TorrentHandle,
  TorrentStatus,
  TorrentState,
  TorrentEvent,
  TorrentEventType,
  TorrentEventListener,
  Unsubscribe,
} from '../torrent-engine';

import { getWssTrackers, mergeWssTrackers, refreshTrackers } from './tracker-registry';
import { triggerBrowserDownload } from '../../../../platform/browser-download';
import { buildMagnetUri } from '../../../protocol/magnet-uri';
import { calculateEta } from '../../../../shared/utils/formatters';


// Throttle: emit at most one progress event per THROTTLE_MS per torrent
const THROTTLE_MS = 300;

/**
 * Active filesystem-write operation for one torrent.
 *
 * Invariant: `completion` resolves only after every FileSystemWritableFileStream
 * belonging to this operation has either successfully closed OR been aborted —
 * including in multi-file failure and cancellation cases.
 *
 * This invariant is enforced by _streamFilesToDirectory using Promise.allSettled(),
 * which waits for ALL file operations to settle before resolving/rejecting.
 * completion resolves in _beginStream's .finally(), which fires after that.
 */
interface StreamOperation {
  /** Aborts all file streams in this operation. Shared across all files. */
  controller: AbortController;
  /** Retained so resume() can restart the stream without re-asking for a destination. */
  destination: TorrentDestination;
  /** Resolves when every writable in this operation has closed or been aborted. */
  completion: Promise<void>;
}

export class WebTorrentEngine implements TorrentEngine {
  private client!: WebTorrent.Instance;
  private listeners = new Set<TorrentEventListener>();
  private progressThrottle = new Map<string, ReturnType<typeof setTimeout>>();
  private configured = false;

  /**
   * Active stream operations (one per active download torrent).
   * Replaces the former `activeStreams: Set<string>` which could only track
   * existence and had no mechanism to cancel the underlying pipeTo().
   */
  private streamOperations = new Map<string, StreamOperation>();

  /**
   * Destination retained across pause/abort so resume() can restart the stream.
   * Written by pause() before aborting; consumed once by resume().
   */
  private pausedDestinations = new Map<string, TorrentDestination>();

  /** Set of paused torrent infoHashes */
  private pausedTorrents = new Set<string>();

  configure(config: TorrentEngineConfig): void {
    // WebTorrent browser config: maxConns only; DHT/PEX not applicable in browser
    this.client = new WebTorrent({
      maxConns: config.maxConnections ?? 55,
    } as any);
    this.configured = true;

    // Proactively warm up the live tracker cache in the background.
    // Result will be available on next getWssTrackers() call.
    refreshTrackers().catch(() => { /* fallback already handled in registry */ });
  }

  private ensureConfigured(): void {
    if (!this.configured) {
      // Auto-configure with defaults if not explicitly configured
      this.configure({});
    }
  }

  async createAndSeed(input: TorrentInput): Promise<TorrentHandle> {
    this.ensureConfigured();
    const { files, trackers = getWssTrackers() } = input;

    if (files.length === 0) {
      throw new Error('No files to share');
    }

    // Extract browser File objects — WebTorrent seed() accepts W3C File[]
    const browserFiles = files.map((f) => {
      if (!f.browserFile) {
        throw new Error(`File "${f.name}" has no browser File object`);
      }
      return f.browserFile;
    });

    return new Promise<TorrentHandle>((resolve) => {
      this.client.seed(
        browserFiles as any,
        { announce: trackers } as any,
        (torrent: any) => {
          this._setupBackpressure(torrent);

          // Wire up upload progress events (seeding state)
          torrent.on('upload', () => {
            this._throttleEmit(torrent, 'torrent:progress', 'seeding');
          });

          torrent.on('error', (err: any) => {
            this._emitError(torrent.infoHash, String(err));
          });

          torrent.on('warning', (warn: any) => {
            // Warnings are non-fatal; log but don't propagate
            console.warn('[WebTorrent] warning:', warn);
          });

          this._emitEvent({
            type: 'torrent:added',
            id: torrent.infoHash,
            timestamp: Date.now(),
            status: this._mapStatus(torrent, 'seeding'),
          });

          resolve({
            engineTorrentId: torrent.infoHash,           // WebTorrent runtime ID
            torrentIdentity: { v1: torrent.infoHash },   // FROM WebTorrent — 40-char hex
            magnetUri: torrent.magnetURI,                // FROM WebTorrent — verbatim
            torrentName: torrent.name,
            torrentFile: torrent.torrentFile ? new Uint8Array(torrent.torrentFile) : undefined,
          });
        }
      );
    });
  }

  async download(
    source: TorrentSource,
    destination: TorrentDestination
  ): Promise<TorrentHandle> {
    this.ensureConfigured();

    // Build the target identifier for WebTorrent.add()
    const magnetUri = source.magnetUri
      ?? (source.infoHash?.v1 ? buildMagnetUri({ infoHash: source.infoHash.v1 }) : undefined);

    const target: any =
      source.torrentBytes ?? magnetUri ?? null;

    if (!target) {
      throw new Error('TorrentSource must provide magnetUri, torrentBytes, or infoHash');
    }

    // Merge WSS trackers from the magnet URI itself + our defaults.
    // This is critical for magnet links that already declare wss:// trackers.
    const announce = mergeWssTrackers(magnetUri);
    const opts: any = { announce };

    return new Promise<TorrentHandle>((resolve) => {
      this.client.add(target, opts, (torrent: any) => {
        this._setupBackpressure(torrent);

        queueMicrotask(() => {
          this._emitEvent({
            type: 'torrent:metadata',
            id: torrent.infoHash,
            timestamp: Date.now(),
            status: this._mapStatus(torrent, 'metadata'),
          });
        });

        // Begin streaming to directory if File System Access API is available.
        // _beginStream is idempotent — guards against duplicate starts.
        if (destination.kind === 'browser-directory') {
          this._beginStream(torrent, destination);
        }

        torrent.on('download', () => {
          this._throttleEmit(torrent, 'torrent:progress', 'downloading');
        });

        torrent.on('done', () => {
          // WebTorrent automatically continues seeding after download.
          this._emitEvent({
            type: 'torrent:completed',
            id: torrent.infoHash,
            timestamp: Date.now(),
            status: this._mapStatus(torrent, 'seeding'),
          });

          if (destination.kind !== 'browser-directory') {
            // Non-directory fallback: trigger browser download via anchor.
            // Must not block the event handler — errors go to _emitError.
            this._anchorDownloadFiles(torrent).catch((err) => {
              this._emitError(torrent.infoHash, String(err));
            });
          }
        });

        torrent.on('error', (err: any) => {
          this._emitError(torrent.infoHash ?? String(target), String(err));
          // NOTE: reject() is intentionally NOT called here.
          // resolve() already fired below — this Promise is already settled.
          // Post-start torrent errors route through the event system:
          //   torrent:error → coordinator event handler → session.status = 'failed'
        });

        resolve({
          engineTorrentId: torrent.infoHash,           // WebTorrent runtime ID
          torrentIdentity: { v1: torrent.infoHash },   // FROM WebTorrent — 40-char hex
          magnetUri: torrent.magnetURI,                // FROM WebTorrent — verbatim
        });
      });
    });
  }

  async pause(id: string): Promise<void> {
    // Clear any pending throttle timer so stale progress events don't fire after pause
    const timer = this.progressThrottle.get(id);
    if (timer) { clearTimeout(timer); this.progressThrottle.delete(id); }

    const t = this.client.get(id) as any;
    if (t) {
      if (typeof t.pause === 'function') t.pause();

      // Fix wire choking behavior: choke all wires and clear pending piece requests
      if (Array.isArray(t.wires)) {
        for (const wire of t.wires) {
          if (typeof wire.choke === 'function') {
            try { wire.choke(); } catch {}
          }
          if (Array.isArray(wire.requests)) {
            wire.requests.length = 0;
          }
        }
      }

      // Fix tracker interval: pause/stop recurring tracker discovery while paused
      if (t.discovery?.tracker) {
        try {
          if (typeof t.discovery.tracker.stop === 'function') {
            t.discovery.tracker.stop();
          } else if (typeof t.discovery.tracker.pause === 'function') {
            t.discovery.tracker.pause();
          }
        } catch {}
      }
    }

    const op = this.streamOperations.get(id);
    if (op) {
      // Retain destination BEFORE aborting — needed to restart the stream on resume.
      this.pausedDestinations.set(id, op.destination);

      // Abort filesystem stream
      op.controller.abort(new DOMException('Paused by user', 'AbortError'));

      // Block until every FileSystemWritableFileStream in this operation has
      // either successfully closed or been aborted. This is the gate that
      // prevents resume() from starting a new stream while an old one is still
      // tearing down (competing file handles for the same destination).
      await op.completion;
    }

    this.pausedTorrents.add(id);

    if (t) {
      this._emitEvent({
        type: 'torrent:paused',
        id,
        timestamp: Date.now(),
        status: this._mapStatus(t, 'paused'),
      });
    }
  }

  async resume(id: string): Promise<void> {
    this.pausedTorrents.delete(id);

    // Guard: if a previous stream operation is somehow still settling
    // (e.g. programmatic/concurrent calls), wait for it to fully complete.
    // Under the normal pause→resume flow, pause() already awaits completion,
    // so this will be a no-op in most cases.
    const existingOp = this.streamOperations.get(id);
    if (existingOp) {
      await existingOp.completion;
    }

    const t = this.client.get(id) as any;
    if (!t) return;

    if (typeof t.resume === 'function') {
      t.resume();
    }

    // Fix wire unchoking behavior on resume
    if (Array.isArray(t.wires)) {
      for (const wire of t.wires) {
        wire._backpressureThrottled = false;
        if (typeof wire.unchoke === 'function') {
          try { wire.unchoke(); } catch {}
        }
      }
    }

    // Fix tracker interval: restart tracker intervals and trigger immediate announce
    if (t.discovery?.tracker) {
      try {
        if (typeof t.discovery.tracker.start === 'function') {
          t.discovery.tracker.start();
        } else if (typeof t.discovery.tracker.resume === 'function') {
          t.discovery.tracker.resume();
        } else if (typeof t.discovery.tracker.announce === 'function') {
          t.discovery.tracker.announce();
        }
      } catch {}
    } else if (typeof t.announce === 'function') {
      try { t.announce(); } catch {}
    }

    if (typeof t._rechoke === 'function') {
      try { t._rechoke(); } catch {}
    }

    this._emitEvent({ type: 'torrent:resumed', id, timestamp: Date.now() });

    // Restart the filesystem stream from the stored destination.
    // idb-chunk-store retains all previously downloaded+verified pieces —
    // they stream to disk instantly. Remaining pieces arrive from peers as
    // the torrent resumes requesting from the swarm.
    const destination = this.pausedDestinations.get(id);
    if (destination) {
      this.pausedDestinations.delete(id); // consumed — _beginStream takes ownership
      this._beginStream(t, destination);
    }
  }

  async stop(id: string): Promise<void> {
    // Stop network but retain data in chunk store
    await this.pause(id);
  }

  async remove(id: string, opts: { deleteData: boolean }): Promise<void> {
    // Abort and await stream cleanup FIRST.
    // File handles must be released before destroyStore can safely clear IDB.
    const op = this.streamOperations.get(id);
    if (op) {
      op.controller.abort(new DOMException('Torrent removed', 'AbortError'));
      await op.completion;
    }
    this.pausedDestinations.delete(id);
    this.pausedTorrents.delete(id);

    try {
      const torrent = await (this.client.get(id) as any);
      if (torrent) {
        await new Promise<void>((resolve, reject) => {
          (this.client as any)._remove(torrent, { destroyStore: opts.deleteData }, (err: any) => {
            if (err) return reject(err);
            resolve();
          });
        });
      }
    } catch {
      // Clean fallback if already removed or non-existent
    }

    this._emitEvent({ type: 'torrent:removed', id, timestamp: Date.now() });
  }

  async getStatus(id: string): Promise<TorrentStatus> {
    const t = this.client.get(id) as any;
    if (!t) throw new Error(`Torrent ${id} not found`);
    const isPaused = this.pausedTorrents.has(id) || Boolean(t.paused);
    const state: TorrentState = isPaused
      ? 'paused'
      : t.done
        ? 'seeding'
        : 'downloading';
    return this._mapStatus(t, state);
  }

  async saveResumeData(_id: string): Promise<Uint8Array | null> {
    // Browser resume is handled by the persistent chunk store (idb-chunk-store).
    // No explicit resume data needed — the store retains piece data across sessions.
    return null;
  }

  async restoreFromResumeData(
    _resumeData: Uint8Array,
    _destination: TorrentDestination
  ): Promise<TorrentHandle> {
    // Browser resume is automatic via persistent store on re-add.
    throw new Error(
      'Browser resume is handled by the persistent chunk store, not resume data blobs'
    );
  }

  onEvent(listener: TorrentEventListener): Unsubscribe {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  async destroy(): Promise<void> {
    // Clear all throttle timers
    this.progressThrottle.forEach((timer) => clearTimeout(timer));
    this.progressThrottle.clear();

    // Abort all active stream operations and await their cleanup before
    // destroying the WebTorrent client. Ensures file handles are released cleanly.
    // Note: guarantees cleanup when destroy() is explicitly called (e.g. app teardown).
    // Browser tab termination does not guarantee async cleanup runs.
    const cleanupOps = Array.from(this.streamOperations.values()).map((op) => {
      op.controller.abort(new DOMException('Engine destroyed', 'AbortError'));
      return op.completion;
    });
    await Promise.allSettled(cleanupOps);
    this.streamOperations.clear();
    this.pausedDestinations.clear();

    return new Promise<void>((resolve) => {
      if (!this.client) {
        resolve();
        return;
      }

      // Cleanly destroy all active torrent wires to prevent RTCPeerConnection leaks
      try {
        if (Array.isArray(this.client.torrents)) {
          for (const t of this.client.torrents) {
            const wires = (t as any)?.wires;
            if (Array.isArray(wires)) {
              for (const wire of wires) {
                try {
                  if (typeof wire.destroy === 'function') wire.destroy();
                } catch (_) {}
              }
            }
          }
        }
      } catch (_) {}

      this.client.destroy((err: any) => {
        if (err) console.error('[WebTorrent] destroy error:', err);
        resolve();
      });
    });
  }

  // ─── Private helpers ─────────────────────────────────────────────

  /**
   * Start (or restart on resume) a filesystem-write operation for a torrent.
   *
   * Idempotent: if an operation is already active for this torrent, returns
   * immediately. The caller should abort the existing operation first if a
   * restart is needed (see pause() → resume() flow).
   */
  private _beginStream(torrent: any, destination: TorrentDestination): void {
    if (destination.kind !== 'browser-directory') return;
    // Idempotency guard: don't start a second stream if one is already active
    if (this.streamOperations.has(torrent.infoHash)) return;

    const controller = new AbortController();

    // completion resolves in .finally() below — only after _streamFilesToDirectory
    // has fully settled via Promise.allSettled(). Every writable is closed or
    // aborted before pause()/remove() is unblocked.
    let resolveCompletion!: () => void;
    const completion = new Promise<void>((res) => { resolveCompletion = res; });

    const op: StreamOperation = { controller, destination, completion };
    this.streamOperations.set(torrent.infoHash, op);

    this._streamFilesToDirectory(
      torrent,
      (destination as any).handle,
      op
    )
      .catch((err: any) => {
        // AbortError = intentional pause/cancel/error-cascade — not an engine error
        if (err?.name !== 'AbortError') {
          this._emitError(torrent.infoHash, String(err));
        }
      })
      .finally(() => {
        // Identity check: only clean up if THIS operation is still the active one.
        // A newer operation (started by resume()) must not be evicted by an old
        // .finally() running after the fact.
        //
        // Without check: Stream A starts → A aborts → B starts → A.finally() deletes B ❌
        // With check:    map.get(id) === B ≠ A → A.finally() does nothing ✅
        if (this.streamOperations.get(torrent.infoHash) === op) {
          this.streamOperations.delete(torrent.infoHash);
        }
        // Unblock any pause() or remove() that is awaiting completion.
        resolveCompletion();
      });
  }

  /**
   * Stream all torrent files to a FileSystemDirectoryHandle in parallel.
   *
   * Key properties:
   * 1. All files pipe concurrently — tf.stream() prioritises pieces on demand,
   *    so downloading and writing begin immediately without waiting for completion.
   * 2. A non-AbortError in any one file aborts the shared controller so all
   *    sibling streams receive AbortError and terminate.
   * 3. Uses Promise.allSettled() — unlike Promise.all(), does NOT return early
   *    on the first rejection. Every file operation must fully settle (close or
   *    abort its writable) before this function resolves. This enforces the
   *    `completion` invariant.
   *
   * Limitation (documented, by design):
   *   The legacy getBlob() fallback is NOT abortable — getBlob() accepts no
   *   AbortSignal. This path is only reached for very old WebTorrent builds
   *   where tf.stream() is unavailable. The primary BitDrop flow always uses
   *   tf.stream(). Building custom cancellation around a callback we don't own
   *   would reinvent the wheel — we explicitly avoid that.
   */
  private async _streamFilesToDirectory(
    torrent: any,
    dirHandle: FileSystemDirectoryHandle,
    op: StreamOperation,
  ): Promise<void> {
    const { signal } = op.controller;

    const fileOps = torrent.files.map(async (tf: any) => {
      const fileHandle = await dirHandle.getFileHandle(tf.name, { create: true });
      const writable = await (fileHandle as any).createWritable();
      const stream: ReadableStream | null = tf.stream?.();

      if (stream) {
        try {
          // Pass signal to pipeTo() — the pipe operation propagates the abort
          // to the readable source and the writable stream per the Web Streams spec.
          // BitDrop additionally calls writable.abort() explicitly in the error path
          // (below) to guarantee cleanup regardless of browser implementation details.
          await stream.pipeTo(writable, { signal });
        } catch (err: any) {
          if (err?.name !== 'AbortError') {
            // Unexpected error in this file stream:
            // 1. Abort the shared controller — all sibling streams receive AbortError.
            op.controller.abort(err);
            // 2. Explicitly abort this writable to discard the .crswap temp file.
            try { await writable.abort(err); } catch { /* preserve original error */ }
          }
          // Re-throw — captured in Promise.allSettled results below.
          throw err;
        }
      } else {
        // ── Legacy getBlob() fallback ──────────────────────────────────────────
        // NOT abortable via AbortController — getBlob() accepts no AbortSignal.
        // Pause/cancel cannot interrupt blob materialisation on this path.
        // Guarantee: pause/cancel stops the streaming path immediately.
        //            The blob path may run to completion before stopping.
        if (signal.aborted) {
          // Best-effort: skip if already aborted before we start
          try { await writable.abort(); } catch { /* ignore */ }
          return;
        }
        await new Promise<void>((resolve, reject) => {
          tf.getBlob((err: any, blob?: Blob) => {
            if (err || !blob) {
              try { writable.abort(err ?? new Error('No blob')).catch(() => {}); } catch {}
              return reject(err ?? new Error('No blob'));
            }
            writable.write(blob)
              .then(() => writable.close())
              .then(resolve)
              .catch(async (writeErr: any) => {
                try { await writable.abort(writeErr); } catch { /* ignore */ }
                reject(writeErr);
              });
          });
        });
      }
    });

    // ── Wait for ALL file operations to settle ────────────────────────────────
    // Promise.allSettled() does NOT return early on the first rejection.
    // Every sibling must fully settle (close or abort its writable) before this
    // function resolves. This enforces the completion invariant: when _beginStream's
    // .finally() fires, every writable is guaranteed to be closed or aborted.
    const results = await Promise.allSettled(fileOps);

    // Re-throw the first non-AbortError if any file failed.
    // AbortErrors are intentional (pause/cancel/error-cascade) — not engine errors.
    const firstRealError = results.find(
      (r): r is PromiseRejectedResult =>
        r.status === 'rejected' && r.reason?.name !== 'AbortError'
    );
    if (firstRealError) throw firstRealError.reason;
  }

  /**
   * Map a WebTorrent torrent object to our TorrentStatus.
   * Uses actual byte counters from WebTorrent — never derives from progress.
   */
  private _mapStatus(t: any, state: TorrentState): TorrentStatus {
    return {
      id: t.infoHash,
      state,
      progress: t.progress ?? 0,
      downloadSpeed: t.downloadSpeed ?? 0, // actual bytes/s
      uploadSpeed: t.uploadSpeed ?? 0, // actual bytes/s
      downloaded: t.downloaded ?? 0, // actual verified bytes
      uploaded: t.uploaded ?? 0, // actual bytes uploaded
      peers: t.numPeers ?? 0,
      seeds: 0, // WebTorrent doesn't expose seed count separately
      eta: calculateEta((t.length ?? 0) - (t.downloaded ?? 0), t.downloadSpeed ?? 0),
      totalBytes: t.length ?? 0,
      magnetUri: t.magnetURI ?? '',
      files: (t.files ?? []).map((f: any) => ({
        name: f.name,
        size: f.length,
        downloaded: f.downloaded ?? 0, // actual bytes per file
        progress: f.progress ?? 0,
      })),
    };
  }

  /** Throttled event emission: at most once per THROTTLE_MS per torrent */
  private _throttleEmit(
    t: any,
    type: TorrentEventType,
    state: TorrentState
  ): void {
    const id = t.infoHash;
    if (this.progressThrottle.has(id)) return;

    this.progressThrottle.set(
      id,
      setTimeout(() => {
        this.progressThrottle.delete(id);
        this._emitEvent({
          type,
          id,
          timestamp: Date.now(),
          status: this._mapStatus(t, state),
        });
      }, THROTTLE_MS)
    );
  }

  private _emitError(id: string, error: string): void {
    this._emitEvent({
      type: 'torrent:error',
      id,
      timestamp: Date.now(),
      error,
    });
  }

  private _emitEvent(event: TorrentEvent): void {
    this.listeners.forEach((l) => {
      try {
        l(event);
      } catch {
        // listener errors must not propagate
      }
    });
  }

  private static readonly BUFFERED_AMOUNT_LOW_THRESHOLD = 1 * 1024 * 1024; // 1 MB
  private static readonly BUFFERED_AMOUNT_HIGH_THRESHOLD = 4 * 1024 * 1024; // 4 MB

  /**
   * Configures WebRTC DataChannel backpressure flow control on peer wires.
   * Sets bufferedAmountLowThreshold to 1MB and throttles piece requests when bufferedAmount > 4MB.
   */
  private _setupBackpressure(torrent: any): void {
    const handleWire = (wire: any) => {
      const channel: RTCDataChannel | undefined =
        wire.conn?._channel ?? wire.conn?.channel ?? wire.channel;

      if (channel) {
        try {
          channel.bufferedAmountLowThreshold = WebTorrentEngine.BUFFERED_AMOUNT_LOW_THRESHOLD;
        } catch {
          // Ignored if setting threshold is unsupported in environment
        }

        const onBufferedAmountLow = () => {
          if (wire._backpressureThrottled) {
            wire._backpressureThrottled = false;
            if (!torrent.paused && typeof wire.unchoke === 'function') {
              wire.unchoke();
            }
          }
        };

        if (typeof channel.addEventListener === 'function') {
          channel.addEventListener('bufferedamountlow', onBufferedAmountLow);
        } else if ('onbufferedamountlow' in channel) {
          channel.onbufferedamountlow = onBufferedAmountLow;
        }
      }

      const checkBackpressure = () => {
        if (!channel) return;
        if (channel.bufferedAmount > WebTorrentEngine.BUFFERED_AMOUNT_HIGH_THRESHOLD) {
          if (!wire._backpressureThrottled) {
            wire._backpressureThrottled = true;
            if (typeof wire.choke === 'function') {
              wire.choke();
            }
          }
        }
      };

      wire.on('download', checkBackpressure);
      wire.on('upload', checkBackpressure);

      if (torrent.paused && typeof wire.choke === 'function') {
        try { wire.choke(); } catch {}
      }
    };

    torrent.on('wire', handleWire);
    if (Array.isArray(torrent.wires)) {
      torrent.wires.forEach(handleWire);
    }
  }

  /**
   * Fallback download for when no FileSystemDirectoryHandle was provided.
   * Replaces memory-bloating URL.createObjectURL(blob) with fsa-chunk-store OPFS streaming
   * when available, or Web Stream download piping, preventing V8 heap exhaustion on >1.5GB files.
   */
  private async _anchorDownloadFiles(torrent: any): Promise<void> {
    for (const tf of torrent.files) {
      try {
        // 1. Try Origin Private File System (OPFS) streaming if available
        if (typeof navigator !== 'undefined' && navigator.storage?.getDirectory) {
          try {
            const root = await navigator.storage.getDirectory();
            const fileHandle = await root.getFileHandle(tf.name, { create: true });
            const writable = await (fileHandle as any).createWritable();
            const stream = typeof tf.stream === 'function' ? tf.stream() : null;
            if (stream) {
              await stream.pipeTo(writable);
              const file = await fileHandle.getFile();
              triggerBrowserDownload(file, tf.name);
              setTimeout(() => {
                root.removeEntry(tf.name).catch(() => {});
              }, 60_000);
              continue;
            }
          } catch (opfsErr) {
            console.warn('[WebTorrent] OPFS streaming fallback failed:', opfsErr);
          }
        }

        // 2. Web Stream download piping / chunked streaming fallback
        await new Promise<void>((resolve) => {
          if (typeof tf.stream === 'function') {
            const stream: ReadableStream = tf.stream();
            const reader = stream.getReader();
            const chunks: Uint8Array[] = [];

            const pump = (): Promise<void> => {
              return reader.read().then(({ done, value }) => {
                if (done) {
                  const blob = new Blob(chunks as BlobPart[]);
                  triggerBrowserDownload(blob, tf.name);
                  resolve();
                  return;
                }
                if (value) chunks.push(value);
                return pump();
              });
            };

            pump().catch((streamErr) => {
              console.error(`[WebTorrent] Web stream piping error for "${tf.name}":`, streamErr);
              resolve();
            });
          } else {
            tf.getBlob((err: any, blob?: Blob) => {
              if (err || !blob) {
                console.error(`[WebTorrent] Failed to get blob for "${tf.name}":`, err);
                resolve();
                return;
              }
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = tf.name;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              setTimeout(() => URL.revokeObjectURL(url), 60_000);
              resolve();
            });
          }
        });
      } catch (err) {
        console.error(`[WebTorrent] _anchorDownloadFiles error for "${tf.name}":`, err);
      }
    }
  }
}

